import { Anchor, Card, createStyles, Group, Stack } from '@mantine/core';
import type { ScheduleSettings } from '@repo/api-codegen';
import { useMobxReaction } from '@repo/common/utils/useMobxReaction';
import { Banner, Select, Text, TextInput } from '@repo/foundations';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { BlockHeader } from '../../components/BlockHeader/BlockHeader';
import { DAYS_OF_WEEK, HOURS_OF_DAY, scheduleTypeOptions } from './constants';
import { ScheduleBlockStore } from './store';

interface ScheduleBlockControllerInternalProps {
	store: ScheduleBlockStore;
	displayTitle?: boolean;
	title?: string;
	onScheduleDelete: () => void;
}

const useStyles = createStyles((theme) => ({
	group: {
		gap: theme.spacing.sm,
		padding: 0,
	},
	card: {
		overflow: 'visible',
	},
	anchor: {
		textDecoration: 'underline',
	},
}));

const ScheduleBlockControllerInternal = observer(
	({
		store,
		displayTitle = true,
		title,
		onScheduleDelete,
	}: ScheduleBlockControllerInternalProps) => {
		const { classes } = useStyles();

		const showCronExpression = useMemo(
			() => store.scheduleSettings.schedule_type === 'cron',
			[store.scheduleSettings.schedule_type]
		);

		const showDayInput = useMemo(
			() => store.scheduleSettings.schedule_type === 'weekly',
			[store.scheduleSettings.schedule_type]
		);

		const showHourInput = useMemo(
			() => ['daily', 'weekly'].includes(store.scheduleSettings.schedule_type),
			[store.scheduleSettings.schedule_type]
		);

		const showHumanReadableSchedule = useMemo(
			() => !!store.humanReadableSchedule,
			[store.humanReadableSchedule]
		);

		const dayAndHourSelectWidth = useMemo(
			() => (showDayInput && showHourInput ? '48.5%' : '100%'),
			[showDayInput, showHourInput]
		);

		const showInvalidCronExpression = useMemo(
			() => store.invalidCronExpression,
			[store.invalidCronExpression]
		);

		const showTooFrequentCronExpression = useMemo(
			() => store.tooFrequentCronExpression,
			[store.tooFrequentCronExpression]
		);

		const helpText = (
			<Text size="xs" color="text/secondary/default">
				Cron syntax: minute hour day month weekday. Learn more at{' '}
				<Anchor
					href="https://crontab.guru"
					target="_blank"
					className={classes.anchor}
				>
					crontab.guru
				</Anchor>
			</Text>
		);

		return (
			<Card withBorder radius="md" pt={0} className={classes.card}>
				{displayTitle && (
					<BlockHeader
						title={title ?? 'Scheduled'}
						iconName="clock"
						onDelete={onScheduleDelete}
						toolTip="Remove trigger"
						backgroundColor="fill/transparent-secondary/default"
					/>
				)}
				<Card.Section p="md">
					<Stack>
						<Select
							label="Frequency"
							value={store.scheduleSettings.schedule_type}
							defaultValue={store.scheduleSettings.schedule_type}
							onChange={store.setScheduleType}
							data={scheduleTypeOptions}
						/>
						{showCronExpression && (
							<TextInput
								label="Cron"
								variant="default"
								size="md"
								placeholder=""
								value={store.cronExpression}
								onChange={(event) =>
									store.setCronExpression(event.target.value)
								}
								help={helpText}
							/>
						)}
						{(showDayInput || showHourInput) && (
							<Group className={classes.group}>
								{showDayInput && (
									<Select
										label="Day"
										variant="default"
										size="md"
										placeholder="Enter a day"
										value={String(store.scheduleSettings.day_of_week ?? 0)}
										defaultValue={String(0)}
										onChange={(value) => store.setDayOfWeek(Number(value))}
										data={DAYS_OF_WEEK}
										width={dayAndHourSelectWidth}
									/>
								)}
								{showHourInput && (
									<Select
										label="Hour"
										variant="default"
										size="md"
										placeholder="Enter an hour"
										value={String(store.scheduleSettings.hour_of_day ?? 0)}
										defaultValue={String(0)}
										onChange={(value) => store.setHourOfDay(Number(value))}
										data={HOURS_OF_DAY}
										width={dayAndHourSelectWidth}
									/>
								)}
							</Group>
						)}
						{showHumanReadableSchedule && !showInvalidCronExpression && (
							<Banner
								message={store.humanReadableSchedule}
								tone="info"
								inCard
							/>
						)}
						{showInvalidCronExpression && (
							<Banner
								message="Invalid cron expression. Please refer to crontab.guru for help."
								tone="critical"
								inCard
							/>
						)}
						{showTooFrequentCronExpression && (
							<Banner
								message="Cron expression must be hourly or less frequent."
								tone="critical"
								inCard
							/>
						)}
					</Stack>
				</Card.Section>
			</Card>
		);
	}
);

interface ScheduleBlockControllerProps
	extends Pick<ScheduleBlockControllerInternalProps, 'displayTitle' | 'title'> {
	onScheduleChange: (schedule: ScheduleSettings) => void;
	onScheduleDelete: () => void;
	initialScheduleSettings: ScheduleSettings;
}

export const ScheduleBlockController = observer(
	({
		onScheduleChange,
		initialScheduleSettings,
		...rest
	}: ScheduleBlockControllerProps) => {
		const [store] = useState(
			() => new ScheduleBlockStore(initialScheduleSettings)
		);

		useMobxReaction(
			useCallback(() => store.scheduleSettings, [store]),
			useCallback(
				(scheduleSettings) => {
					onScheduleChange(toJS(scheduleSettings));
				},
				[onScheduleChange]
			)
		);

		return <ScheduleBlockControllerInternal {...rest} store={store} />;
	}
);
